import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, User, FolderOpen, Undo2, List } from "lucide-react";
import PageTitle from "../components/common/PageTitle";
import LoadingOverlay from "../components/common/LoadingOverlay";
import { formatDateShort } from "../utils/dateFormat";
import { showError } from "../utils/toast";
import { API_BASE_URL } from "../api/apiClient";
import { ROUTES } from "../config/constants";

export default function DashboardDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [lesson, setLesson] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    loadLesson();
  }, [id]);

  useEffect(() => {
    if (lesson) {
      loadCategories();
    }
  }, [lesson]);

  async function loadLesson() {
    if (!id) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/lessons/${id}`);
      if (!response.ok) throw new Error("Lesson not found");
      const data = await response.json();
      setLesson(data.lesson || data);
    } catch (err) {
      console.error("Failed to load lesson:", err);
      showError("Gagal memuat data lesson");
      navigate(ROUTES.LESSONS_DASHBOARD);
    } finally {
      setLoading(false);
    }
  }

  async function loadCategories() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/categories`);
      const data = await response.json();
      if (Array.isArray(data) && lesson) {
        const cat = data.find((c) => c.id === lesson.category_id);
        setCategory(cat);
      }
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  }

  if (loading || !lesson) {
    return <LoadingOverlay isLoading={true} />;
  }

  return (
    <div className="container mx-auto px-4 py-6 pt-20">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(ROUTES.LESSONS_DASHBOARD)}
          className="flex items-center text-sm gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <Undo2 size={14} />
          <span>Kembali ke Dashboard</span>
        </button>

        <div className="flex items-start justify-between">
          <PageTitle>{lesson.case || "-"}</PageTitle>
        </div>
      </div>

      {/* Metadata */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Calendar size={16} />
            <span>Dibuat: {formatDateShort(lesson.created_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            <User size={16} />
            <span>Dibuat oleh: {lesson.user_name || "Unknown"}</span>
          </div>
          <div className="flex items-center gap-2">
            <List size={16} />
            <span>
              Kategori: {category?.name || "-"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FolderOpen size={16} />
            <span>
              Project: {lesson.project_name || "-"}
              {lesson.project_division && (
                <span className="text-gray-400 ml-1">({lesson.project_division})</span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        {/* Penyebab */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Penyebab / Root Cause
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">{lesson.causes}</p>
        </div>

        {/* Dampak */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Dampak / Impact
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">{lesson.effect}</p>
        </div>

        {/* Tindakan */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Tindakan yang Dilakukan
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">
            {lesson.project_action}
          </p>
        </div>

        {/* Rekomendasi */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Rekomendasi untuk Masa Depan
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">
            {lesson.future_recommendation}
          </p>
        </div>
      </div>

      {/* Photo Gallery */}
      {lesson.photos && lesson.photos.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Foto Dokumentasi ({lesson.photos.length})
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {lesson.photos.map((photo, index) => (
              <div
                key={index}
                className="relative group cursor-pointer"
                onClick={() => setSelectedPhoto(`${API_BASE_URL}/${photo}`)}
              >
                <img
                  src={`${API_BASE_URL}/${photo}`}
                  alt={`Dokumentasi ${index + 1}`}
                  className="w-full h-48 object-cover rounded-lg border border-gray-300 hover:border-blue-500 transition-colors"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-lg transition-all flex items-center justify-center">
                  <span className="text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    Klik untuk memperbesar
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={selectedPhoto}
              alt="Enlarged"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute text-sm top-4 right-4 bg-white/90 text-gray-900 rounded-full px-3 py-1.5 hover:bg-gray-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
